import {useEffect, useMemo, useState} from "react";
import {axiosInstance} from "config/api";

const user = JSON.parse(localStorage.getItem("user"));
const frontdesk_id = user?.role === "front_desk" ? user.id : null;
const role = user?.role;
const isFrontdesk = role === "front_desk";
const isdoctor = role === "doctor";
const user_email = user?.email
// Helper: normalize various date representations to YYYY-MM-DD (local date semantics)
function toDateOnly(raw) {
    if (!raw && raw !== 0) return null;
    const s = String(raw).trim();
    // already yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    // ISO-ish with time
    if (s.includes("T")) return s.split("T")[0];
    if (s.includes(" ")) return s.split(" ")[0];
    // numeric timestamp
    if (/^\d+$/.test(s)) {
        const d = new Date(Number(s));
        if (!isNaN(d)) return d.toISOString().slice(0, 10);
    }
    // fallback: try Date parser
    const d = new Date(s);
    if (!isNaN(d)) return d.toISOString().slice(0, 10);
    return null;
}

// Compare by yyyy-mm-dd strings by numericizing YYYYMMDD
function yyyymmddToNumber(dstr) {
    if (!dstr) return null;
    return Number(dstr.replace(/-/g, ""));
}

// Normalize bill object to a known shape
function normalizeBill(b) {
    const bill = {...b};
    // normalize id fields
    // doctor may be provided as doctor_id, doctorId, or as an object/primitive in `doctor`.
    let docId = b.doctor_id ?? b.doctorId ?? null;
    if (!docId && b.doctor) {
        if (typeof b.doctor === "object") {
            docId = b.doctor.id ?? b.doctor._id ?? b.doctor.doctor_id ?? null;
        } else {
            // could be numeric string or a name; accept numeric-like values
            const s = String(b.doctor).trim();
            if (/^\d+$/.test(s)) docId = s;
            else docId = null; // if it's a name, don't treat as id
        }
    }
    bill.doctor_id = docId !== undefined ? docId : null;
    bill.clinic_id = b.clinic_id ?? b.clinicId ?? b.clinic ?? null;
    bill.appointment_date =
        b.appointment_date ?? b.payment_date ?? b.created_date ?? b.createdAt ?? b.date ?? null;
    bill.total_amt = b.total_amt ?? b.totalAmount ?? b.bill_amount ?? b.total_amount ?? b.netAmount ?? 0;
    bill.patient_name = b.patient_name ?? b.patient ?? b.name ?? "";
    bill.doctor_name = b.doctor_name ?? b.doctor ?? b.doctorName ?? "";
    if (user_email.includes(".main"))  return bill;      
    if (isdoctor) return bill;
    // frontdesk: only return bills assigned to them
    if (b.frontdesk_id === frontdesk_id) {
        return bill;
    }
    return [];
}

// === Request deduplication to avoid duplicate fetches (e.g., React Strict Mode double-mount) ===
const inFlightRequests = new Map();

/**
 * Deduplicated GET request using a Map keyed by request identity.
 * Returns the same promise for concurrent requests with the same key.
 */
function fetchBillsDeduped(url, config, key) {
    if (inFlightRequests.has(key)) {
        return inFlightRequests.get(key);
    }
    const p = axiosInstance.get(url, config)
        .finally(() => {
            inFlightRequests.delete(key);
        });
    inFlightRequests.set(key, p);
    return p;
}

export default function useBills(options = {}) {
    const {
        clinicId = null,
        doctorId = null,
        doctorIds = [],
        fromDate = null,
        toDate = null,
        searchQuery = "",
        autoFetch = true,
    } = options;
    const {debug = false} = options;

    const [rawBills, setRawBills] = useState([]);
    const [bills, setBills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch bills from API when filters change
    useEffect(() => {
        if (!autoFetch) return;
        let canceled = false;
        const token = localStorage.getItem("token");
        setLoading(true);
        setError(null);

        const params = {};
        if (doctorId) params.doctor_id = doctorId;
        else if (Array.isArray(doctorIds) && doctorIds.length === 1)
            params.doctor_id = doctorIds[0];
        // if (clinicId) params.clinic_id = clinicId;

        const reqKey = `bills:${token || ""}:${JSON.stringify(params)}`;

        fetchBillsDeduped("api/bills/getAll", {
                headers: {Authorization: token ? `Bearer ${token}` : ""},
                params,
            }, reqKey)
            .then((res) => {
                if (canceled) return;
                const list = res?.data?.bills ?? res?.data ?? [];
                if (debug) console.debug("useBills: fetched", Array.isArray(list) ? list.length : 0, "raw items", {params});
                const normalized = (Array.isArray(list) ? list : []).map(normalizeBill);
                setRawBills(normalized);
            })
            .catch((err) => {
                if (canceled) return;
                setError(err);
                setRawBills([]);
            })
            .finally(() => {
                if (!canceled) setLoading(false);
            });

        return () => {
            canceled = true;
        };
    }, [clinicId, doctorId, JSON.stringify(doctorIds), fromDate, toDate, autoFetch]);

    // Client-side filtering (dates, search, clinic/doctor matching)
    const filtered = useMemo(() => {
        if (!rawBills || !Array.isArray(rawBills)) return [];

        // prepare range
        const from = fromDate ? toDateOnly(fromDate) : null;
        const to = toDate ? toDateOnly(toDate) : null;
        const fromNum = yyyymmddToNumber(from);
        const toNum = yyyymmddToNumber(to);
        const q = (searchQuery || "").toLowerCase();

        return rawBills.filter((bill) => {
            const nb = bill;
            const billDateStr = toDateOnly(nb.appointment_date);
            if (!billDateStr) return false;
            const billNum = yyyymmddToNumber(billDateStr);
            if (fromNum && toNum) {
                if (billNum < fromNum || billNum > toNum) return false;
            }

            //   if (clinicId && String(nb.clinic_id) !== String(clinicId)) return false;

            // effective doctor list check
            //   if (Array.isArray(doctorIds) && doctorIds.length > 0) {
            //     const match = doctorIds.some((id) => String(nb.doctor_id) === String(id));
            //     if (!match) return false;
            //   } else if (doctorId) {
            //     if (String(nb.doctor_id) !== String(doctorId)) return false;
            //   }

            if (q) {
                const name = String(nb.patient_name || "").toLowerCase();
                const dname = String(nb.doctor_name || "").toLowerCase();
                if (!name.includes(q) && !dname.includes(q)) return false;
            }

            return true;
        });
    }, [rawBills, clinicId, doctorId, JSON.stringify(doctorIds), fromDate, toDate, searchQuery]);

    // Optional debug: log filtered result counts
    useEffect(() => {
        if (!debug) return;
        try {
            console.debug("useBills: rawBills", rawBills.length, "filtered", filtered.length, {
                clinicId,
                doctorId,
                doctorIds,
                fromDate,
                toDate,
                searchQuery
            });
        } catch (e) {
            /* ignore */
        }
    }, [debug, rawBills, filtered, clinicId, doctorId, JSON.stringify(doctorIds), fromDate, toDate, searchQuery]);

    // Compute stats
    const stats = useMemo(() => {
        const chosen = filtered;
        const totalRevenue = (chosen || []).reduce((sum, bill) => {
            const receiptPresent = !!(bill.payment_receipt || bill.payment_receipt_urls || bill.payment_receipt_url);
            const action = String((bill.payment_action || bill.payment_status || bill.appointment_status || "")).toLowerCase();
            const isPaid = receiptPresent || action === "paid";
            const amount = isPaid ? parseFloat(bill.total_amt || 0) || 0 : 0;
            return sum + amount;
        }, 0);

        const paid = (chosen || []).filter((b) => {
            const action = String((b.payment_action || b.payment_status || b.appointment_status || "")).toLowerCase();
            const receipt = !!(b.payment_receipt || b.payment_receipt_urls || b.payment_receipt_url);
            return action === "paid" || receipt;
        }).length;

        const pending = (chosen || []).filter((b) => String((b.payment_action || b.payment_status || b.appointment_status || "")).toLowerCase() === "pending").length;

        const booked = (chosen || []).filter((b) => String((b.payment_action || b.payment_status || b.appointment_status || "")).toLowerCase() === "booked").length;

        return {totalRevenue, paidBills: paid, pendingBills: pending, bookedBills: booked};
    }, [filtered]);

    useEffect(() => {
        setBills(filtered);
    }, [filtered]);

    return {
        bills,
        rawBills,
        loading,
        error,
        stats,
        refetch: () => {
            // simple way to re-trigger effect: toggle a dummy state would be better but callers can change deps
            // For now, we can call the fetch by setting a small timeout to let callers update filters
            return Promise.resolve();
        },
    };
}
